import ExcelJS from "exceljs";
import QRCode from "qrcode-svg";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
const logoLSD = "/assets/media/image/logo-LSD.png";
const logoLMC = "/assets/media/image/LMC-LOGO.jpg";

export const getWritePermission = () => {
  let storedPermissions = localStorage.getItem("permissionId");
  const permissionValues1 =
    JSON.parse(localStorage.getItem("rolePermissions")) || {};
  let canWriteValue = null;

  storedPermissions = parseInt(storedPermissions, 10);
  const matchingPermission = permissionValues1.find(
    (mapping) => mapping.permissionId === storedPermissions
  );

  if (matchingPermission) {
    canWriteValue = matchingPermission.canWrite;
  } else {
    console.log(`No match found for permission ID ${storedPermissions}`);
  }
  return canWriteValue;
};

export const downloadExcel = async (vehicleDetails) => {
  // Create a new workbook and add a worksheet
  const currentDate = new Date();
  const formattedDate = `${currentDate.getDate()}/${
    currentDate.getMonth() + 1
  }/${currentDate.getFullYear()}`;
  const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
  const cumulativeHeadingReport = `Vehicle Details`;
  const downloadDateTime = `Vehicle Report Generated on: ${formattedDate}, ${formattedTime}`;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Vehicle Details");

  // Define styles
  const boldFont = { bold: true };

  // Add the downloadDateTime to the correct row and columns, and ensure formatting
  worksheet.addRow(["", "", downloadDateTime, ""]);
  worksheet.mergeCells(1, 2, 1, 9); // Merge cells for downloadDateTime from column 2 to 9
  worksheet.getCell(1, 2).value = downloadDateTime;
  worksheet.getCell(1, 2).font = boldFont; // Set font style
  worksheet.getCell(1, 2).alignment = { horizontal: "center" };

  // Add "Vehicle Details" to the correct row and columns, and ensure formatting
  worksheet.addRow(["", "", "Vehicle Details", ""]);
  worksheet.mergeCells(2, 2, 2, 9); // Merge cells for "Vehicle Details"
  worksheet.getCell(2, 2).value = "Vehicle Details";
  worksheet.getCell(2, 2).font = boldFont;
  worksheet.getCell(2, 2).alignment = { horizontal: "center" };

  // Add an empty row for spacing
  worksheet.addRow([""]);

  // Add table header row with bold font
  const tableHeaderRow = worksheet.addRow([
    "S.No",
    "Vehicle Number",
    "Vehicle UID",
    "Vehicle Type",
    "Vehicle Capacity",
    "Zone",
    "Ward",
    "Collection Type",
    "Owner Type",
    "Driver Name",
    "Driver Number",
    "Primary Deployment Incharge Name",
    "Primary Deployment Incharge Number",
    "Transfer Station",
    "Status",
  ]);

  tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = boldFont;
    cell.alignment = { horizontal: "left" }; // Left-align the cell content
    if (colNumber >= 1 && colNumber <= 15) {
      // Apply background color to specific cells
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F6B1B1" }, // Light red background color
      };
    }
  });

  // Add data rows from vehicleDetails
  vehicleDetails?.forEach((item, index) => {
    worksheet
      .addRow([
        index + 1, // S.No
        item.vehicleNumber || "N/A",
        item.vehicleUuid || "N/A",
        item.vehicleTypeName || "N/A",
        item.vehicleCapacityName || "N/A",
        item.zoneName || "N/A",
        item.circleName || "N/A", // Ward (using circleName as ward)
        item.collectionTypeName || "N/A",
        item.ownerTypeName || "N/A",
        item.driverName || "N/A",
        item.driverNumber || "N/A",
        item.secondaryDeploymentInchargeName || "N/A",
        item.secondaryDeploymentInchargeNumber || "N/A",
        item.transferStationName || "N/A",
        item.isActive ? "Active" : "InActive", // Status
      ])
      .eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.alignment = { horizontal: "left" };
      });
  });

  worksheet.columns = [
    { width: 8 }, // S.No
    { width: 20 }, // Vehicle Number
    { width: 30 }, // Vehicle UID
    { width: 20 }, // Vehicle Type
    { width: 20 }, // Vehicle Capacity
    { width: 15 }, // Zone
    { width: 15 }, // Ward
    { width: 20 }, // Collection Type
    { width: 15 }, // Owner Type
    { width: 25 }, // Driver Name
    { width: 20 }, // Driver Number
    { width: 35 }, // Primary Deployment Incharge Name
    { width: 25 }, // Primary Deployment Incharge Number
    { width: 25 }, // Transfer Station
    { width: 15 }, // Status
  ];

  // Save the workbook to a file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "VehicleDetails.xlsx"; // The filename for the downloaded Excel
  a.click();
  URL.revokeObjectURL(url); // Clean up the blob URL
};

const options = { day: "numeric", month: "numeric", year: "numeric" };

const generationTime = ` ${new Date()
  .toLocaleString("en-GB", options)
  .replace(/\//g, "/")}`;

export const downloadQRCodeImage = (vehicle) => { 
  try {
    const uuid = vehicle?.vehicleUuid;
    const qrSize = 512; // Reduced from 1024 to 512 for smaller file size

    const qr = new QRCode({
      content: uuid,
      padding: 4,
      width: qrSize,
      height: qrSize,
      color: "#000000",
      background: "#ffffff",
      ecl: "M",
    });

    const svg = qr.svg();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Use 2x resolution for good quality while keeping file size reasonable
      const scaleFactor = 2;
      const highResSize = qrSize * scaleFactor;
      canvas.width = highResSize;
      canvas.height = highResSize;
      
      // Disable all smoothing to prevent visible lines
      ctx.imageSmoothingEnabled = false;
      ctx.imageSmoothingQuality = 'low';
      
      // Draw at high resolution
      ctx.drawImage(img, 0, 0, highResSize, highResSize);

      // Use JPEG with good quality (0.85) for much better compression than PNG
      // JPEG is perfect for QR codes (black/white) and reduces file size significantly
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      
      // Calculate content height to remove bottom space
      const headerHeight = 18;
      const qrWidthMM = 65;
      const rowHeight = 8;
      const labelsCount = 3; // VEHICLE TYPE, VEH. REG. NO, ZONE & WARD
      const tableHeight = labelsCount * rowHeight;
      const bottomMargin = 5;
      const calculatedHeight = headerHeight + qrWidthMM + tableHeight + bottomMargin;
      
      // Set page size to match content height (no extra space at bottom)
      const pdf = new jsPDF('p', 'mm', [90, calculatedHeight]);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Load left logo
      const leftLogo = new Image();
      leftLogo.src = logoLSD;
      leftLogo.crossOrigin = 'anonymous';

      // Load right logo
      const rightLogo = new Image();
      rightLogo.src = logoLMC;
      rightLogo.crossOrigin = 'anonymous';

      leftLogo.onload = () => {
          rightLogo.onload = () => {
              // === Header with light beige background ===
              pdf.setFillColor(255, 230, 174); // Light beige color
              pdf.rect(0, 0, pageWidth, 18, 'F');
              
              // Add left logo with more space from text
              pdf.addImage(leftLogo, 'PNG', 3, 3, 12, 12);

              
              // Add right logo with more space from text
              pdf.addImage(rightLogo, 'PNG', pageWidth - 15, 3, 12, 12);

              // Main title in dark red (with proper spacing)
              pdf.setFont('helvetica', 'bold');
              pdf.setTextColor(153, 0, 0); // Dark red color
              pdf.setFontSize(9);
              pdf.text('LUCKNOW MUNICIPAL CORPORATION', pageWidth / 2, 7, { align: 'center' });

              // Subtitle in black (with more space from title)
              pdf.setFontSize(8);
              pdf.setTextColor(0, 0, 0);
              pdf.text('LUCKNOW SWACHHATA ABHIYAN', pageWidth / 2, 13, { align: 'center' });

              // === QR Code in center - large size ===
                 const qrX = (pageWidth - qrWidthMM) / 2;
                 const qrY = 18; // Positioned directly below header with no gap
                 pdf.addImage(imgData, 'PNG', qrX, qrY, qrWidthMM, qrWidthMM);
                

          // ==== Labels and Values ====
          const labels = [
            // "OWNER TYPE:",
            "VEHICLE TYPE:",
            "VEH. REG. NO :",
            "ZONE & WARD  :",
            // "WARD:",
            // "VEHICLE CAPACITY:",
            // "UID:",
          ];
          const values = [
            // vehicle?.ownerTypeName ?? "-",
            vehicle?.vehicleTypeName ?? "-",
            vehicle?.vehicleNumber ?? "-",
            (vehicle?.zoneName + " & " + (vehicle?.wardName || vehicle?.circleName)) ?? "-",
            // (vehicle?.wardName || vehicle?.circleName) ?? "-",
            // vehicle?.vehicleCapacityName ?? "-",
            // vehicle?.vehicleUuid ?? "-",
          ];

          // Create table with thin borders - narrow and centered, directly below QR code
          const tableY = qrY + qrWidthMM + 0;
          const tableWidth = 70; // Narrow table width
          const tableX = (pageWidth - tableWidth) / 2; // Center the table
          const labelWidth = 35; // Width for label column
          const valueWidth = tableWidth - labelWidth; // Width for value column

          // Thin gray borders
          pdf.setDrawColor(200, 200, 200); // Light gray color for borders
          pdf.setLineWidth(0.2); // Thin line

          for (let i = 0; i < labels.length; i++) {
            const y = tableY + i * rowHeight;
            
            // Draw table border for each row (thin gray border)
            pdf.rect(tableX, y, tableWidth, rowHeight);
            
            // Draw vertical line to separate label and value columns
            pdf.line(tableX + labelWidth, y, tableX + labelWidth, y + rowHeight);
            
            // Add label in left column - bold, black, left-aligned
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0); // Black text
            pdf.text(labels[i], tableX + 2, y + 5);
            
            // Add value in right column - bold, black, left-aligned
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0); // Black text
            // Use splitTextToSize to wrap long text within cell width
            const wrappedValue = pdf.splitTextToSize(values[i] || "-", valueWidth - 4);
            pdf.text(wrappedValue, tableX + labelWidth + 2, y + 5);
          }

          // ==== Footer ====
          pdf.setFontSize(3);
          pdf.setTextColor(100);
          pdf.setFont("helvetica", "normal");
          // pdf.text(
          //   "Powered by VMax e-Solutions India Pvt Limited",
          //   pageWidth / 2,
          //   74,
          //   { align: "center" }
          // );

          // Save PDF
          pdf.save(`qr-code-${vehicle?.vehicleNumber}.pdf`);
        };

        leftLogo.onerror = (e) => console.error("LMC Logo load failed:", e);
      };

      rightLogo.onerror = (e) => console.error("Logo load failed:", e);
    };

    img.onerror = (err) => console.error("QR image load failed:", err);

    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  } catch (error) {
    console.error("Error generating QR PDF:", error);
  }
};

export const generateQRCodeSVG = (vehicleUuid) => {
  try {
    const qr = new QRCode({
      content: vehicleUuid,
      width: 400,
      height: 400,
      color: "#000000",
    });
    return qr.svg();
  } catch (error) {
    console.error("Error generating QR code:", error);
    return "<svg></svg>";
  }
};

export const handleDownloadQRCode = async (vehicles, onProgress) => {
  const zip = new JSZip();
  const qrSize = 512; // Reduced from 1024 to 512 for smaller file size
  const limitedVehicles = vehicles.slice(0, 5000);
  const chunkSize = 10; // Process 10 vehicles at a time
  const totalVehicles = limitedVehicles.length;

  const logoURL = logoLSD;
  const lmcLogoURL = logoLMC;

  // Load logo
  const logo = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoURL;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

  // Load LMC logo
  const lmcLogo = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = lmcLogoURL;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

  // Process vehicles in chunks
  for (let i = 0; i < totalVehicles; i += chunkSize) {
    const chunk = limitedVehicles.slice(i, i + chunkSize);

    const chunkPromises = chunk.map((vehicle, chunkIndex) => {
      return new Promise((resolve, reject) => {
        const qr = new QRCode({
          content: vehicle?.vehicleUuid || "",
          padding: 4,
          width: qrSize,
          height: qrSize,
          color: "#000000",
          background: "#ffffff",
          ecl: "M",
        });

        const svg = qr.svg();
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
          // Use 2x resolution for good quality while keeping file size reasonable
          const scaleFactor = 2;
          const highResSize = qrSize * scaleFactor;
          canvas.width = highResSize;
          canvas.height = highResSize;
          
          // Get fresh context after setting dimensions (context resets when dimensions change)
          const renderCtx = canvas.getContext("2d");
          
          // Disable all smoothing to prevent visible lines
          renderCtx.imageSmoothingEnabled = false;
          renderCtx.imageSmoothingQuality = 'low';
          
          // Draw at high resolution
          renderCtx.drawImage(img, 0, 0, highResSize, highResSize);

          // Use JPEG with good quality (0.85) for much better compression than PNG
          // JPEG is perfect for QR codes (black/white) and reduces file size significantly
          const imgData = canvas.toDataURL('image/jpeg', 0.85);
          
          // Calculate content height to remove bottom space
          const headerHeight = 18;
          const qrWidthMM = 65;
          const rowHeight = 8;
          const labelsCount = 3; // VEHICLE TYPE, VEH. REG. NO, ZONE & WARD
          const tableHeight = labelsCount * rowHeight;
          const bottomMargin = 5;
          const calculatedHeight = headerHeight + qrWidthMM + tableHeight + bottomMargin;
          
          // Set page size to match content height (no extra space at bottom)
          const pdf = new jsPDF("p", "mm", [90, calculatedHeight]);
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          // === Header with light beige background ===
          pdf.setFillColor(255, 230, 174); // Light beige color
          pdf.rect(0, 0, pageWidth, 18, 'F');
          
          // Add left logo with more space from text
          pdf.addImage(logo, 'PNG', 3, 3, 12, 12);
          
          // Add right logo with more space from text
          pdf.addImage(lmcLogo, 'PNG', pageWidth - 15, 3, 12, 12);

          // Main title in dark red (with proper spacing)
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(153, 0, 0); // Dark red color
          pdf.setFontSize(9);
          pdf.text('LUCKNOW MUNICIPAL CORPORATION', pageWidth / 2, 7, { align: 'center' });

          // Subtitle in black (with more space from title)
          pdf.setFontSize(8);
          pdf.setTextColor(0, 0, 0);
          pdf.text('LUCKNOW SWACHHATA ABHIYAN', pageWidth / 2, 13, { align: 'center' });

          // === QR Code in center - large size ===
          const qrX = (pageWidth - qrWidthMM) / 2;
          const qrY = 18; // Positioned directly below header with no gap
          // Add high-resolution image to maintain quality when zooming
          pdf.addImage(imgData, 'PNG', qrX, qrY, qrWidthMM, qrWidthMM);

          // ==== Labels and Values ====
          const labels = [
            // "OWNER TYPE:",
            "VEHICLE TYPE:",
            "VEH. REG. NO :",
            "ZONE & WARD  :",
            // "WARD:",
            // "VEHICLE CAPACITY:",
            // "UID:",
          ];
          const values = [
            // vehicle?.ownerTypeName ?? "-",
            vehicle?.vehicleTypeName ?? "-",
            vehicle?.vehicleNumber ?? "-",
            (vehicle?.zoneName + " & " + (vehicle?.wardName || vehicle?.circleName)) ?? "-",
            // (vehicle?.wardName || vehicle?.circleName) ?? "-",
            // vehicle?.vehicleCapacityName ?? "-",
            // vehicle?.vehicleUuid ?? "-",
          ];

          // Create table with thin borders - narrow and centered, directly below QR code
          const tableY = qrY + qrWidthMM + 0;
          const tableWidth = 70; // Narrow table width
          const tableX = (pageWidth - tableWidth) / 2; // Center the table
          const labelWidth = 35; // Width for label column
          const valueWidth = tableWidth - labelWidth; // Width for value column

          // Thin gray borders
          pdf.setDrawColor(200, 200, 200); // Light gray color for borders
          pdf.setLineWidth(0.2); // Thin line

          for (let j = 0; j < labels.length; j++) {
            const y = tableY + j * rowHeight;
            
            // Draw table border for each row (thin gray border)
            pdf.rect(tableX, y, tableWidth, rowHeight);
            
            // Draw vertical line to separate label and value columns
            pdf.line(tableX + labelWidth, y, tableX + labelWidth, y + rowHeight);
            
            // Add label in left column - bold, black, left-aligned
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0); // Black text
            pdf.text(labels[j], tableX + 2, y + 5);
            
            // Add value in right column - bold, black, left-aligned
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0); // Black text
            // Use splitTextToSize to wrap long text within cell width
            const wrappedValue = pdf.splitTextToSize(values[j] || "-", valueWidth - 4);
            pdf.text(wrappedValue, tableX + labelWidth + 2, y + 5);
          }

          // === Footer ===
          pdf.setFontSize(3);
          pdf.setTextColor(100);
          pdf.setFont("helvetica", "normal");
          // pdf.text(
          //   "Powered by VMax e-Solutions India Pvt Limited",
          //   pageWidth / 2,
          //   74,
          //   { align: "center" }
          // );

          // Enable PDF compression to reduce file size
          const pdfBlob = pdf.output("blob", { compress: true });
          const globalIndex = i + chunkIndex;
          zip.file(
            `${globalIndex + 1}_${vehicle.vehicleNumber || "unknown"}.pdf`,
            pdfBlob
          );
          resolve();
        };

        img.onerror = reject;
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      });
    });

    // Wait for current chunk to complete
    await Promise.all(chunkPromises);

    // Update progress
    const processedCount = Math.min(i + chunkSize, totalVehicles);
    const progress = Math.round((processedCount / totalVehicles) * 100);
    if (onProgress) {
      onProgress(progress, processedCount, totalVehicles);
    }

    // Small delay to prevent UI freezing
    await new Promise((resolve) => setTimeout(resolve, 10));
  }

  try {
    // Use higher compression for ZIP file
    const content = await zip.generateAsync({ 
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 9 }
    });
    saveAs(content, "qrcodes.zip");
    console.log("ZIP file with PDFs generated and downloaded successfully.");
  } catch (error) {
    console.error("Error generating or downloading ZIP file:", error);
  }
};
